/* eslint-disable @typescript-eslint/no-explicit-any */
import mjml2html from "mjml-browser";

const escapeHtml = (value: unknown): string =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// Outlook Classic `mso-highlight` strictly accepts only 15 specific color names, not HEX or RGB values.
// We map any Rich Text background color via Euclidean RGB distance to the closest supported Outlook color.
const MSO_COLORS: { name: string; r: number; g: number; b: number }[] = [
  { name: "black", r: 0, g: 0, b: 0 },
  { name: "blue", r: 0, g: 0, b: 255 },
  { name: "turquoise", r: 0, g: 255, b: 255 },
  { name: "brightgreen", r: 0, g: 255, b: 0 },
  { name: "pink", r: 255, g: 0, b: 255 },
  { name: "red", r: 255, g: 0, b: 0 },
  { name: "yellow", r: 255, g: 255, b: 0 },
  { name: "white", r: 255, g: 255, b: 255 },
  { name: "darkblue", r: 0, g: 0, b: 128 },
  { name: "teal", r: 0, g: 128, b: 128 },
  { name: "green", r: 0, g: 128, b: 0 },
  { name: "violet", r: 128, g: 0, b: 128 },
  { name: "darkred", r: 128, g: 0, b: 0 },
  { name: "darkyellow", r: 128, g: 128, b: 0 },
  { name: "gray-25", r: 192, g: 192, b: 192 },
  { name: "gray-50", r: 128, g: 128, b: 128 },
];

const getClosestMsoColor = (colorStr: string): string | null => {
  let r: number | null = null,
    g: number | null = null,
    b: number | null = null;

  if (colorStr.startsWith("rgb")) {
    const match = colorStr.match(/\d+/g);
    if (match && match.length >= 3) {
      r = parseInt(match[0], 10);
      g = parseInt(match[1], 10);
      b = parseInt(match[2], 10);
    }
  } else if (colorStr.startsWith("#")) {
    const hex = colorStr.replace("#", "");
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length === 6) {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    }
  } else {
    const directMatch = MSO_COLORS.find(
      (c) => c.name === colorStr.toLowerCase(),
    );
    if (directMatch) return directMatch.name;
  }

  if (r === null || g === null || b === null) return null; // Could not parse – no highlight

  let closestName = "yellow";
  let minDistance = Infinity;

  for (const c of MSO_COLORS) {
    const distance =
      Math.pow(r - c.r, 2) + Math.pow(g - c.g, 2) + Math.pow(b - c.b, 2);
    if (distance < minDistance) {
      minDistance = distance;
      closestName = c.name;
    }
  }
  return closestName;
};

// Returns false for non-color / "no highlight" background values that Quill emits when resetting highlight
const isRealBackgroundColor = (colorStr: string): boolean => {
  const s = colorStr.toLowerCase().trim();
  if (
    !s ||
    s === "transparent" ||
    s === "inherit" ||
    s === "initial" ||
    s === "unset" ||
    s === "none"
  )
    return false;
  // rgba(0,0,0,0) and rgb(0,0,0) alpha=0 are fully transparent
  if (/^rgba?\(\s*0\s*,\s*0\s*,\s*0\s*,\s*0\s*\)$/.test(s)) return false;
  return true;
};

/**
 * Sanitize HTML content so it is safe to embed inside <mj-text>.
 * MJML's <mj-text> supports basic inline/block HTML (p, span, a, strong, em, br, img, etc.)
 * but NOT deeply nested structures like <table>, <figure>, <div> trees, etc.
 * Also cleans up inline style attributes that crash juice (MJML's CSS inliner).
 */
const sanitizeForMjml = (html: string): string => {
  if (!html || html.trim() === "") return html;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<body>${html}</body>`, "text/html");

    // --- Step 1: Unwrap block-level elements that MJML can't handle inside mj-text ---
    const blockTags = new Set([
      "TABLE",
      "TBODY",
      "THEAD",
      "TFOOT",
      "TR",
      "TD",
      "TH",
      "FIGURE",
      "FIGCAPTION",
      "SECTION",
      "ARTICLE",
      "NAV",
      "ASIDE",
      "MAIN",
      "HEADER",
      "FOOTER",
    ]);

    const unwrapElement = (el: Element) => {
      const children = Array.from(el.children);
      for (const child of children) {
        unwrapElement(child);
      }

      if (blockTags.has(el.tagName)) {
        const fragment = doc.createDocumentFragment();
        while (el.firstChild) {
          fragment.appendChild(el.firstChild);
        }
        if (["TD", "TH", "TR"].includes(el.tagName)) {
          fragment.appendChild(doc.createElement("br"));
        }
        el.parentNode?.replaceChild(fragment, el);
      }
    };

    unwrapElement(doc.body);

    // --- Step 2: Strip class attributes and FILTER inline styles ---
    // MJML's juice CSS inliner crashes on many CSS properties from imported HTML.
    // However, we MUST preserve basic formatting from the Rich Text editor (React Quill)
    // like 'color' and 'background-color' applied to <span> tags.
    const allElements = doc.body.querySelectorAll("*");
    allElements.forEach((el) => {
      const style = el.getAttribute("style");
      if (style) {
        const safeStyles: string[] = [];
        const colorMatch = style.match(/(?:^|[^a-z-])color\s*:\s*([^;]+);?/i);
        const bgMatch = style.match(
          /(?:^|[^a-z-])background(?:-color)?\s*:\s*([^;]+);?/i,
        );
        if (colorMatch) safeStyles.push(`color: ${colorMatch[1].trim()}`);
        if (bgMatch) {
          const bgColor = bgMatch[1].trim();
          if (isRealBackgroundColor(bgColor)) {
            safeStyles.push(`background-color: ${bgColor}`);
            const msoColor = getClosestMsoColor(bgColor);
            if (msoColor) safeStyles.push(`mso-highlight: ${msoColor}`);
          }
        }

        if (safeStyles.length > 0) {
          el.setAttribute("style", safeStyles.join("; "));
        } else {
          el.removeAttribute("style");
        }
      }
      el.removeAttribute("class");
    });

    // --- Step 3: Reset <p> margins so Quill output doesn't double-space with mj-text padding ---
    // Browsers (and MJML) apply a default margin of ~13px top/bottom to <p> tags.
    // When the component already has explicit paddingTop/paddingBottom set on mj-text,
    // the <p> margin stacks on top of it, producing more whitespace than expected.
    // We zero out the margin here; line-height on the mj-text attribute handles visual breathing room.
    doc.body.querySelectorAll("p").forEach((p) => {
      const existingStyle = p.getAttribute("style") || "";
      if (!existingStyle.includes("margin")) {
        p.setAttribute(
          "style",
          existingStyle ? `${existingStyle}; margin: 0` : "margin: 0",
        );
      }
    });

    // --- Step 4: Clean up excessive <br> sequences ---
    let result = doc.body.innerHTML;
    result = result.replace(/(<br\s*\/?>\s*){3,}/gi, "<br><br>");

    return result;
  } catch {
    // If parsing fails, return original HTML as fallback
    return html;
  }
};

const getById = (content: any[], id: string) =>
  content.find((x) => x?.id === id);

const getZoneItems = (data: any, zoneKey: string): any[] => {
  // Legacy support for migrations, but default to the root content array
  const refs = data?.zones?.[zoneKey] || data?.content || [];
  const content = Array.isArray(data?.content) ? data.content : [];

  if (!Array.isArray(refs)) return [];

  return refs
    .map((ref: any) => {
      if (!ref) return null;
      if (typeof ref === "string") return getById(content, ref);
      if (typeof ref === "object" && ref.type) return ref;
      if (typeof ref === "object" && ref.id)
        return getById(content, ref.id) ?? ref;
      return null;
    })
    .filter(Boolean);
};

const renderHeroImageInner = (props: any, rootWidth: string): string => {
  const url = escapeHtml(props?.url || "");
  return `
    <mj-section padding="0" background-color="transparent">
      <mj-column width="100%" padding="0">
        <mj-image src="${url}" padding="0" width="${rootWidth}" />
      </mj-column>
    </mj-section>
  `;
};

const renderTitleInner = (props: any): string => {
  const text = sanitizeForMjml(props?.text || "");
  const fontFamily = escapeHtml(
    props?.fontFamily || "open Sans Helvetica, Arial, sans-serif",
  );
  const fontSize = escapeHtml(props?.fontSize || "32px");
  const lineHeight = escapeHtml(props?.lineHeight || "1.2");
  const textAlign = escapeHtml(props?.textAlign || "center");
  const color = escapeHtml(props?.color || "#000000");
  const paddingTop = escapeHtml(props?.paddingTop || "50px");
  const paddingBottom = escapeHtml(props?.paddingBottom || "30px");
  const paddingLeft = escapeHtml(props?.paddingLeft || "25px");
  const paddingRight = escapeHtml(props?.paddingRight || "25px");

  return `
    <mj-section padding="0" background-color="transparent">
      <mj-column width="100%" padding="0">
        <mj-text align="${textAlign}" color="${color}" font-family="${fontFamily}" font-size="${fontSize}" line-height="${lineHeight}" padding="${paddingTop} ${paddingRight} ${paddingBottom} ${paddingLeft}">
          ${text}
        </mj-text>
      </mj-column>
    </mj-section>
  `;
};

const renderSubtitleInner = (props: any): string => {
  const text = sanitizeForMjml(props?.text || "");
  const fontFamily = escapeHtml(
    props?.fontFamily || "open Sans Helvetica, Arial, sans-serif",
  );
  const fontSize = escapeHtml(props?.fontSize || "22px");
  const lineHeight = escapeHtml(props?.lineHeight || "1.4");
  const textAlign = escapeHtml(props?.textAlign || "left");
  const color = escapeHtml(props?.color || "#000000");
  const paddingTop = escapeHtml(props?.paddingTop || "20px");
  const paddingBottom = escapeHtml(props?.paddingBottom || "8px");
  const paddingLeft = escapeHtml(props?.paddingLeft || "25px");
  const paddingRight = escapeHtml(props?.paddingRight || "25px");

  return `
    <mj-section padding="0" background-color="transparent">
      <mj-column width="100%" padding="0">
        <mj-text align="${textAlign}" color="${color}" font-family="${fontFamily}" font-size="${fontSize}" line-height="${lineHeight}" padding="${paddingTop} ${paddingRight} ${paddingBottom} ${paddingLeft}">
          ${text}
        </mj-text>
      </mj-column>
    </mj-section>
  `;
};

const renderTextInner = (props: any): string => {
  const text = sanitizeForMjml(props?.text || "");
  const fontFamily = escapeHtml(
    props?.fontFamily || "open Sans Helvetica, Arial, sans-serif",
  );
  const fontSize = escapeHtml(props?.fontSize || "15px");
  const lineHeight = escapeHtml(props?.lineHeight || "1.4");
  const textAlign = escapeHtml(props?.textAlign || "left");
  const color = escapeHtml(props?.color || "#000000");
  const paddingTop = escapeHtml(props?.paddingTop || "20px");
  const paddingBottom = escapeHtml(props?.paddingBottom || "8px");
  const paddingLeft = escapeHtml(props?.paddingLeft || "25px");
  const paddingRight = escapeHtml(props?.paddingRight || "25px");

  return `
    <mj-section padding="0" background-color="transparent">
      <mj-column width="100%" padding="0">
        <mj-text align="${textAlign}" color="${color}" font-family="${fontFamily}" font-size="${fontSize}" line-height="${lineHeight}" padding="${paddingTop} ${paddingRight} ${paddingBottom} ${paddingLeft}">
          ${text}
        </mj-text>
      </mj-column>
    </mj-section>
  `;
};

const renderCtaInner = (props: any): string => {
  let buttonLabel = sanitizeForMjml(props?.label || "");
  // Strip all block-level tags (p, br) from button label — keep inline formatting only.
  // Quill can produce trailing <p><br></p> that inflates mj-button height in some clients.
  buttonLabel = buttonLabel
    .replace(/<\/?(p|br)[^>]*>/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  const buttonUrl = escapeHtml(props?.url || "");
  const bgColor = escapeHtml(props?.bgColor || "#414141");
  const textColor = escapeHtml(props?.textColor || "#ffffff");
  const paddingV = escapeHtml(props?.paddingVertical || "10px");
  const paddingH = escapeHtml(props?.paddingHorizontal || "25px");
  const textAlign = escapeHtml(props?.textAlign || "center");
  const fontFamily = escapeHtml(
    props?.fontFamily || "open Sans Helvetica, Arial, sans-serif",
  );
  const fontSize = escapeHtml(props?.fontSize || "15px");
  const paddingTop = escapeHtml(props?.paddingTop || "8px");
  const paddingBottom = escapeHtml(props?.paddingBottom || "8px");
  const paddingLeft = escapeHtml(props?.paddingLeft || "25px");
  const paddingRight = escapeHtml(props?.paddingRight || "25px");

  return `
    <mj-section padding="0" background-color="transparent">
      <mj-column width="100%" padding="0">
        <mj-button href="${buttonUrl === "#" ? "" : buttonUrl}" align="${textAlign}" background-color="${bgColor}" color="${textColor}" border-radius="0px" font-family="${fontFamily}" font-size="${fontSize}" inner-padding="${paddingV} ${paddingH}" padding="${paddingTop} ${paddingRight} ${paddingBottom} ${paddingLeft}">
          ${buttonLabel}
        </mj-button>
      </mj-column>
    </mj-section>
  `;
};

const renderHyperlinkInner = (props: any): string => {
  const preText = sanitizeForMjml(props?.preText || "");
  const linkLabel = escapeHtml(props?.linkLabel || "clicca qui");
  const url = escapeHtml(props?.url || "#");
  const linkColor = escapeHtml(props?.linkColor || "#0066cc");
  const fontFamily = escapeHtml(
    props?.fontFamily || "open Sans Helvetica, Arial, sans-serif",
  );
  const fontSize = escapeHtml(props?.fontSize || "15px");
  const textAlign = escapeHtml(props?.textAlign || "left");
  const color = escapeHtml(props?.color || "#000000");
  const paddingTop = escapeHtml(props?.paddingTop || "8px");
  const paddingBottom = escapeHtml(props?.paddingBottom || "8px");
  const paddingLeft = escapeHtml(props?.paddingLeft || "25px");
  const paddingRight = escapeHtml(props?.paddingRight || "25px");

  const fullText = preText
    ? `<span style="color:${color}; font-family:${fontFamily}; font-size:${fontSize};">${preText} </span><a href="${url}" style="color:${linkColor}; text-decoration:underline; font-family:${fontFamily}; font-size:${fontSize};">${linkLabel}</a>`
    : `<a href="${url}" style="color:${linkColor}; text-decoration:underline; font-family:${fontFamily}; font-size:${fontSize};">${linkLabel}</a>`;

  return `
    <mj-section padding="0" background-color="transparent">
      <mj-column width="100%" padding="0">
        <mj-text align="${textAlign}" color="${color}" font-family="${fontFamily}" font-size="${fontSize}" padding="${paddingTop} ${paddingRight} ${paddingBottom} ${paddingLeft}">
          ${fullText}
        </mj-text>
      </mj-column>
    </mj-section>
  `;
};

const renderImageWithTextInner = (props: any, rootWidthPx: number): string => {
  const imageUrl = escapeHtml(props?.imageUrl || "");
  const imageAlt = escapeHtml(props?.imageAlt || "");
  const imageWidth = escapeHtml(props?.imageWidth || "40%");
  const text = sanitizeForMjml(props?.text || ""); // Not escaped because it allows HTML, but sanitized for MJML
  const layout = escapeHtml(props?.layout || "image-left");
  const fontFamily = escapeHtml(props?.fontFamily || "Arial,sans-serif");
  const fontSize = escapeHtml(props?.fontSize || "15px");
  const lineHeight = escapeHtml(props?.lineHeight || "1.5");
  const fontWeight = escapeHtml(props?.fontWeight || "normal");
  const fontStyle = escapeHtml(props?.fontStyle || "normal");
  const textDecoration = escapeHtml(props?.textDecoration || "none");
  const textAlign = escapeHtml(props?.textAlign || "left");
  const color = escapeHtml(props?.color || "#000000");
  const paddingTop = escapeHtml(props?.paddingTop || "8px");
  const paddingBottom = escapeHtml(props?.paddingBottom || "8px");
  const paddingLeft = escapeHtml(props?.paddingLeft || "25px");
  const paddingRight = escapeHtml(props?.paddingRight || "25px");

  const imgPctStr = String(imageWidth).replace("%", "");
  const imgPct = parseInt(imgPctStr) || 40;
  const txtPct = 100 - imgPct;
  const imgPx = Math.floor((rootWidthPx * imgPct) / 100);

  const isTopLayout = layout === "image-top" || imgPct === 100;

  let columns = "";
  if (isTopLayout) {
    columns = `
      <mj-column width="100%" padding="0">
        ${imageUrl ? `<mj-image src="${imageUrl}" alt="${imageAlt}" width="${imgPx}px" fluid-on-mobile="true" padding="${text ? "0 0 20px 0" : "0"}" />` : ""}
        ${
          text
            ? `<mj-text align="${textAlign}" color="${color}" font-family="${fontFamily}" font-size="${fontSize}" font-style="${fontStyle}" font-weight="${fontWeight}" text-decoration="${textDecoration}" padding="0" line-height="${lineHeight}">
          ${text}
        </mj-text>`
            : ""
        }
      </mj-column>
    `;
  } else if (layout === "image-right") {
    columns = `
      ${
        text
          ? `<mj-column width="${txtPct}%" padding="0" vertical-align="top">
        <mj-text align="${textAlign}" color="${color}" font-family="${fontFamily}" font-size="${fontSize}" font-style="${fontStyle}" font-weight="${fontWeight}" text-decoration="${textDecoration}" padding="0" line-height="${lineHeight}">
          ${text}
        </mj-text>
      </mj-column>`
          : ""
      }
      <mj-column width="${imgPct}%" padding="0" vertical-align="top">
        ${imageUrl ? `<mj-image src="${imageUrl}" alt="${imageAlt}" width="${imgPx}px" fluid-on-mobile="true" padding="${text ? "0 0 0 20px" : "0"}" />` : ""}
      </mj-column>
    `;
  } else {
    columns = `
      <mj-column width="${imgPct}%" padding="0" vertical-align="top">
        ${imageUrl ? `<mj-image src="${imageUrl}" alt="${imageAlt}" width="${imgPx}px" fluid-on-mobile="true" padding="${text ? "0 20px 0 0" : "0"}" />` : ""}
      </mj-column>
      ${
        text
          ? `<mj-column width="${txtPct}%" padding="0" vertical-align="top">
        <mj-text align="${textAlign}" color="${color}" font-family="${fontFamily}" font-size="${fontSize}" font-style="${fontStyle}" font-weight="${fontWeight}" text-decoration="${textDecoration}" padding="0" line-height="${lineHeight}">
          ${text}
        </mj-text>
      </mj-column>`
          : ""
      }
    `;
  }

  return `
    <mj-section padding="${paddingTop} ${paddingRight} ${paddingBottom} ${paddingLeft}" background-color="transparent">
      ${columns}
    </mj-section>
  `;
};

const renderDividerInner = (props: any): string => {
  const borderColor = escapeHtml(props?.borderColor || "#cccccc");
  const borderWidth = escapeHtml(props?.borderWidth || "1px");
  const borderStyle = escapeHtml(props?.borderStyle || "solid");
  const paddingTop = escapeHtml(props?.paddingTop || "20px");
  const paddingBottom = escapeHtml(props?.paddingBottom || "20px");
  const paddingLeft = escapeHtml(props?.paddingLeft || "25px");
  const paddingRight = escapeHtml(props?.paddingRight || "25px");

  return `
    <mj-section padding="0" background-color="transparent">
      <mj-column width="100%" padding="0">
        <mj-divider border-width="${borderWidth}" border-style="${borderStyle}" border-color="${borderColor}" padding="${paddingTop} ${paddingRight} ${paddingBottom} ${paddingLeft}" />
      </mj-column>
    </mj-section>
  `;
};

const formatHtml = (html: string): string => {
  const TAB = "  ";
  const VOID = new Set([
    "area",
    "base",
    "br",
    "col",
    "embed",
    "hr",
    "img",
    "input",
    "link",
    "meta",
    "param",
    "source",
    "track",
    "wbr",
  ]);
  const tokens: string[] = [];
  let i = 0;
  while (i < html.length) {
    if (html[i] !== "<") {
      const end = html.indexOf("<", i);
      const text = (end === -1 ? html.slice(i) : html.slice(i, end)).trim();
      if (text) tokens.push(text);
      i = end === -1 ? html.length : end;
    } else if (html.startsWith("<!--", i)) {
      const end = html.indexOf("-->", i + 4);
      if (end === -1) {
        tokens.push(html.slice(i));
        break;
      }
      tokens.push(html.slice(i, end + 3));
      i = end + 3;
    } else if (html.startsWith("<!", i)) {
      const end = html.indexOf(">", i);
      if (end === -1) {
        tokens.push(html.slice(i));
        break;
      }
      tokens.push(html.slice(i, end + 1));
      i = end + 1;
    } else {
      let j = i + 1;
      let inStr: string | null = null;
      while (j < html.length) {
        const c = html[j];
        if (inStr) {
          if (c === inStr) inStr = null;
        } else if (c === '"' || c === "'") {
          inStr = c;
        } else if (c === ">") {
          j++;
          break;
        }
        j++;
      }
      tokens.push(html.slice(i, j));
      i = j;
    }
  }
  let indent = 0;
  const lines: string[] = [];
  for (const token of tokens) {
    if (token.startsWith("<!--") || token.startsWith("<!")) {
      lines.push(TAB.repeat(indent) + token);
    } else if (token.startsWith("</")) {
      indent = Math.max(0, indent - 1);
      lines.push(TAB.repeat(indent) + token);
    } else if (token.startsWith("<")) {
      const tagName = (token.match(/^<([\w-]+)/) || [])[1]?.toLowerCase() ?? "";
      lines.push(TAB.repeat(indent) + token);
      if (!token.endsWith("/>") && !VOID.has(tagName)) indent++;
    } else {
      lines.push(TAB.repeat(indent) + token);
    }
  }
  return lines.join("\n");
};

export const generateEmailHtml = (data: any): string => {
  const items = getZoneItems(data, "root:body") || [];
  const rootProps = data?.root?.props || {};
  const bgColor = escapeHtml(rootProps?.bgColor || "#ffffff");
  const bgImage = escapeHtml(rootProps?.bgImage || "");
  const bodyBgColor = escapeHtml(rootProps?.bodyBgColor || "#ffffff");
  const bodyBgImage = escapeHtml(rootProps?.bodyBgImage || "");
  const rootWidth = escapeHtml(rootProps?.rootWidth || "600px");
  const rootWidthPx = parseInt(rootWidth.replace("px", "")) || 600;

  let mjmlSections = "";

  items.forEach((item, index) => {
    let innerHtml = "";
    if (item.type === "HeroImage")
      innerHtml = renderHeroImageInner(item.props, rootWidth);
    else if (item.type === "Title") innerHtml = renderTitleInner(item.props);
    else if (item.type === "Subtitle")
      innerHtml = renderSubtitleInner(item.props);
    else if (item.type === "Text") innerHtml = renderTextInner(item.props);
    else if (item.type === "CTA") innerHtml = renderCtaInner(item.props);
    else if (item.type === "Hyperlink")
      innerHtml = renderHyperlinkInner(item.props);
    else if (item.type === "ImageWithText")
      innerHtml = renderImageWithTextInner(item.props, rootWidthPx);
    else if (item.type === "Divider")
      innerHtml = renderDividerInner(item.props);

    if (innerHtml) {
      mjmlSections += innerHtml + "\n";
    }
  });

  const bodyAttrs = `background-color="${bgColor}" width="${rootWidth}"`;
  const wrapperAttrs = `background-color="${bodyBgColor}"${bodyBgImage ? ` background-url="${bodyBgImage}" background-repeat="no-repeat" background-size="cover"` : ""}`;

  const mjmlStr = `
<mjml>
  <mj-head>
    <mj-attributes>
      <mj-all font-family="open Sans Helvetica, Arial, sans-serif"></mj-all>
      <mj-text font-weight="400" font-size="15px" color="#000000" line-height="1"></mj-text>
      <mj-section padding="0px"></mj-section>
    </mj-attributes>
    <mj-style inline="inline">
      p { margin: 13px 0; }
    </mj-style>
  </mj-head>
  <mj-body ${bodyAttrs}>
    <mj-section padding="10px 0" background-color="transparent"></mj-section>
    <mj-wrapper ${wrapperAttrs} padding="0px" css-class="inner-wrapper">
      ${mjmlSections}
    </mj-wrapper>
    <mj-section padding="10px 0" background-color="transparent"></mj-section>
  </mj-body>
</mjml>
  `;

  try {
    const { html, errors } = mjml2html(mjmlStr, {
      validationLevel: "soft",
      beautify: true,
      minify: false,
      keepComments: false,
    });
    if (errors && errors.length > 0) {
      console.warn("MJML validation warnings:", errors);
    }

    let finalHtml = html;

    const escapeRegex = (string: string) => {
      return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
    };

    // Inject outer background explicitly for maximum compatibility
    if (bgImage) {
      const bgCss = `background-image: url('${bgImage}'); background-size: cover; background-position: center top; background-repeat: no-repeat;`;
      // Update body tag
      finalHtml = finalHtml.replace(
        /<body([^>]*)style="([^"]*)"/i,
        `<body$1style="$2 ${bgCss}"`,
      );
      // Update global body wrapper div generated by MJML
      finalHtml = finalHtml.replace(
        /<div([^>]*)aria-roledescription="email"([^>]*)style="([^"]*)"/i,
        `<div$1aria-roledescription="email"$2style="$3 ${bgCss}"`,
      );

      // Inject Outlook VML background handling with aspect="atleast" for 'cover' effect instead of 'tile'
      // By using type="frame" instead of "tile", we prevent Outlook from repeating the image.
      const vmlBg = `<!--[if mso | IE]>
<v:background xmlns:v="urn:schemas-microsoft-com:vml" fill="t">
  <v:fill type="frame" src="${bgImage}" color="${bgColor}" />
</v:background>
<![endif]-->`;
      finalHtml = finalHtml.replace(/(<body[^>]*>)/i, `$1\n    ${vmlBg}`);
    }

    if (bodyBgImage) {
      // Fix VML repeating issue for the inner wrapper. MJML generates `<v:fill type="tile" ...>` by default.
      // We want pure `type="frame"` for stretched cover behavior without tiling.
      finalHtml = finalHtml.replace(
        new RegExp(
          `(<v:fill[^>]*src=["'](?:(?!["']).)*${escapeRegex(bodyBgImage)}["'][^>]*>)`,
          "gi",
        ),
        (match) => {
          let newMatch = match;

          // Force type="frame"
          if (newMatch.match(/type="[^"]+"/i)) {
            newMatch = newMatch.replace(/type="[^"]+"/i, 'type="frame"');
          } else {
            newMatch = newMatch.replace(/<v:fill/i, '<v:fill type="frame"');
          }

          // Strip origin, position, aspect, size which commonly break Outlook's frame sizing
          newMatch = newMatch.replace(/\\s*origin="[^"]*"/gi, "");
          newMatch = newMatch.replace(/\\s*position="[^"]*"/gi, "");
          newMatch = newMatch.replace(/\\s*aspect="[^"]*"/gi, "");
          newMatch = newMatch.replace(/\\s*size="[^"]*"/gi, "");

          return newMatch;
        },
      );
    }

    // Ensure the output always starts with DOCTYPE (mjml-browser may omit it)
    if (!finalHtml.trimStart().toLowerCase().startsWith("<!doctype")) {
      finalHtml = "<!DOCTYPE html>\n" + finalHtml;
    }

    return formatHtml(finalHtml);
  } catch (err) {
    console.error("MJML Compilation Error", err);
    return "<!-- MJML Compilation Error -->";
  }
};
